
<template>
  <div
    class="pns-wrapper"
    role="search"
    ref="searchWrapper"
  >

    <!-- SEARCH INPUT -->
    <form
      class="pn-search pns-form"
      @submit.prevent="submitSearch"
    >
      <svg
        class="pns-input-icon"
        viewBox="0 0 24 24"
        fill="none"
      >
        <circle cx="11" cy="11" r="7"/>
        <path d="m16 16 5 5"/>
      </svg>

      <input
        ref="searchInput"
        v-model="searchQuery"
        type="search"
        aria-label="Rechercher des pneus"
        aria-autocomplete="list"
        aria-controls="pns-results"
        :aria-expanded="showDropdown ? 'true' : 'false'"
        placeholder="Marque, modèle, dimension..."
        autocomplete="off"
        @focus="handleFocus"
        @keydown.down.prevent="moveSelection(1)"
        @keydown.up.prevent="moveSelection(-1)"
        @keydown.esc="closeDropdown"
        @keydown.enter.prevent="handleEnter"
      />

      <button
        v-if="searchQuery"
        type="button"
        class="pns-clear"
        aria-label="Effacer la recherche"
        @click="clearSearch"
      >
        ×
      </button>

      <button
        type="submit"
        class="pn-search-button pns-submit"
        aria-label="Lancer la recherche"
      >
        <svg viewBox="0 0 24 24" fill="none">
          <circle cx="10.8" cy="10.8" r="7"/>
          <path d="m16 16 5 5"/>
        </svg>
      </button>
    </form>

    <!-- LIVE RESULTS -->
    <transition name="pns-dropdown">
      <div
        v-if="showDropdown"
        id="pns-results"
        class="pns-dropdown"
      >

        <!-- HEADER -->
        <div class="pns-dropdown-header">
          <span>
            <svg viewBox="0 0 24 24" fill="none">
              <path d="M4 6h16M4 12h11M4 18h7"/>
            </svg>
            Résultats de recherche
          </span>

          <small>{{ matchingProducts.length }} trouvé(s)</small>
        </div>

        <!-- SUGGESTED BRANDS -->
        <div
          v-if="matchingBrands.length"
          class="pns-brand-section"
        >
          <span class="pns-section-title">MARQUES</span>

          <div class="pns-brand-list">
            <button
              v-for="brand in matchingBrands"
              :key="brand"
              type="button"
              class="pns-brand-chip"
              @click="openBrand(brand)"
            >
              {{ brand }}
              <span>↗</span>
            </button>
          </div>
        </div>

        <!-- PRODUCT LIST -->
        <div v-if="matchingProducts.length" class="pns-results">
          <div class="pns-section-heading">
            <span class="pns-section-title">PNEUS</span>
            <small>Suggestions</small>
          </div>

          <button
            v-for="(product, index) in visibleProducts"
            :key="product.id"
            type="button"
            class="pns-result-item"
            :class="{ selected: selectedIndex === index }"
            @mouseenter="selectedIndex = index"
            @click="openProductSearch(product)"
          >

            <!-- PRODUCT PHOTO -->
            <span class="pns-product-image">
              <img
                v-if="product.image"
                :src="product.image"
                :alt="product.brand + ' ' + product.name"
                loading="lazy"
                @error="hideImage"
              />

              <svg
                v-else
                viewBox="0 0 24 24"
                fill="none"
              >
                <circle cx="12" cy="12" r="9"/>
                <circle cx="12" cy="12" r="4"/>
                <path d="M12 3v5M12 16v5"/>
              </svg>
            </span>

            <!-- PRODUCT INFO -->
            <span class="pns-product-info">
              <strong>
                {{ product.brand }}
                {{ product.name }}
              </strong>

              <span class="pns-product-specs">
                {{ formatSize(product) }}
                <span class="pns-dot">•</span>
                {{ seasonLabel(product.season) }}
              </span>

              <span class="pns-product-tag">
                {{ product.brand }}
              </span>
            </span>

            <!-- PRICE -->
            <span class="pns-product-right">
              <strong v-if="product.price != null">
                {{ formatPrice(product.price) }}
              </strong>

              <small>Voir le pneu</small>
              <svg viewBox="0 0 24 24" fill="none">
                <path d="M4 12h16m-7-7 7 7-7 7"/>
              </svg>
            </span>

          </button>
        </div>

        <!-- NO MATCH -->
        <div
          v-if="!matchingProducts.length && !matchingBrands.length"
          class="pns-empty"
        >
          <div class="pns-empty-icon">
            <svg viewBox="0 0 24 24" fill="none">
              <circle cx="11" cy="11" r="7"/>
              <path d="m16 16 5 5"/>
            </svg>
          </div>

          <strong>Aucun résultat trouvé</strong>
          <p>
            Essayez une autre marque, un modèle ou
            une dimension comme 205/55 R16.
          </p>
        </div>

        <!-- FOOTER -->
        <button
          type="button"
          class="pns-view-all"
          @click="submitSearch"
        >
          <span>
            Voir tous les résultats pour
            <strong>« {{ searchQuery }} »</strong>
          </span>

          <svg viewBox="0 0 24 24" fill="none">
            <path d="M4 12h16m-7-7 7 7-7 7"/>
          </svg>
        </button>

      </div>
    </transition>

  </div>
</template>

<script>
import products from "../../data/products";

function normalize(value) {
  return String(value == null ? "" : value)
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim();
}

function compact(value) {
  return normalize(value).replace(/[^a-z0-9]/g, "");
}

function productSize(product) {
  if (product.size) {
    return String(product.size);
  }

  const width = product.width || "";
  const height = product.height || "";
  const diameter = product.diameter || "";

  if (width && height && diameter) {
    return width + "/" + height + " R" + diameter;
  }

  return "";
}

function searchableText(product) {
  return [
    product.brand,
    product.name,
    product.model,
    productSize(product),
    product.season,
    product.load_index,
    product.speed_index,
    product.description,
  ]
    .filter(Boolean)
    .join(" ");
}

export default {
  name: "NavbarSearch",

  data() {
    return {
      searchQuery: "",
      isFocused: false,
      selectedIndex: -1,
      maxResults: 6,
    };
  },

  computed: {
    hasQuery() {
      // Start searching from the very first character.
      return this.searchQuery.trim().length >= 1;
    },

    showDropdown() {
      return this.isFocused && this.hasQuery;
    },

    matchingProducts() {
      if (!this.hasQuery) return [];

      const query = normalize(this.searchQuery);
      const tokens = query.split(/\s+/).filter(Boolean);
      const compactQuery = compact(query);

      return products
        .filter((product) => {
          const text = normalize(searchableText(product));
          const compressed = compact(searchableText(product));

          return (
            tokens.every((token) => {
              return (
                text.includes(token) ||
                compressed.includes(compact(token))
              );
            }) ||
            (
              compactQuery.length > 0 &&
              compressed.includes(compactQuery)
            )
          );
        })
        .sort((a, b) => {
          // Brand names beginning with the query appear first.
          const brandA = normalize(a.brand);
          const brandB = normalize(b.brand);

          const aStarts = brandA.startsWith(query) ? 1 : 0;
          const bStarts = brandB.startsWith(query) ? 1 : 0;

          return bStarts - aStarts;
        });
    },

    visibleProducts() {
      return this.matchingProducts.slice(0, this.maxResults);
    },

    matchingBrands() {
      if (!this.hasQuery) return [];

      const query = normalize(this.searchQuery);

      const brands = [
        "Michelin",
        "Continental",
        "Bridgestone",
        "Goodyear",
        "Pirelli",
        "Hankook",
        "Dunlop",
        "Kleber",
        "Kumho",
        "Nexen",
      ];

      return brands
        .filter((brand) => normalize(brand).includes(query))
        .slice(0, 5);
    },
  },

  watch: {
    searchQuery() {
      this.selectedIndex = -1;
    },

    "$route.fullPath"() {
      this.closeDropdown();
    },
  },

  mounted() {
    document.addEventListener(
      "mousedown",
      this.handleOutsideClick
    );
  },

  beforeDestroy() {
    document.removeEventListener(
      "mousedown",
      this.handleOutsideClick
    );
  },

  methods: {
    handleFocus() {
      this.isFocused = true;
    },

    closeDropdown() {
      this.isFocused = false;
      this.selectedIndex = -1;
    },

    handleOutsideClick(event) {
      const wrapper = this.$refs.searchWrapper;

      if (wrapper && !wrapper.contains(event.target)) {
        this.closeDropdown();
      }
    },

    clearSearch() {
      this.searchQuery = "";
      this.selectedIndex = -1;

      this.$nextTick(() => {
        if (this.$refs.searchInput) {
          this.$refs.searchInput.focus();
        }
      });
    },

    moveSelection(direction) {
      if (!this.showDropdown) return;

      const total = this.visibleProducts.length;

      if (!total) return;

      this.selectedIndex += direction;

      if (this.selectedIndex >= total) {
        this.selectedIndex = 0;
      }

      if (this.selectedIndex < 0) {
        this.selectedIndex = total - 1;
      }
    },

    handleEnter() {
      if (
        this.showDropdown &&
        this.selectedIndex >= 0 &&
        this.visibleProducts[this.selectedIndex]
      ) {
        this.openProductSearch(
          this.visibleProducts[this.selectedIndex]
        );
        return;
      }

      this.submitSearch();
    },

    submitSearch() {
      const query = this.searchQuery.trim();

      if (!query) return;

      this.navigate({
        name: "products",
        query: { q: query },
      });
    },

    openBrand(brand) {
      this.navigate({
        name: "products",
        query: { brand },
      });
    },

    openProductSearch(product) {
      // The project does not yet have a ProductDetails route.
      // Use brand + tyre model to find the selected product.
      const term = [
        product.brand,
        product.name,
      ].filter(Boolean).join(" ");

      this.navigate({
        name: "products",
        query: { q: term },
      });
    },

    navigate(location) {
      this.closeDropdown();

      this.$router.push(location).catch((error) => {
        if (error && error.name !== "NavigationDuplicated") {
          console.error(error);
        }
      });
    },

    hideImage(event) {
      event.target.style.display = "none";
    },

    formatSize(product) {
      const size = productSize(product);

      const load = product.load_index || "";
      const speed = product.speed_index || "";

      return (size + " " + load + speed).trim() || "Dimensions à consulter";
    },

    seasonLabel(season) {
      return {
        summer: "Pneu été",
        winter: "Pneu hiver",
        "all-season": "4 saisons",
      }[season] || "Pneumatique";
    },

    formatPrice(value) {
      const price = Number(value);

      if (!Number.isFinite(price)) return "";

      return new Intl.NumberFormat("fr-FR", {
        style: "currency",
        currency: "EUR",
      }).format(price);
    },
  },
};
</script>

<style scoped>
.pns-wrapper {
  position: relative;
  width: 100%;
  min-width: 0;
  font-family: "Poppins", sans-serif;
}

.pns-wrapper *,
.pns-wrapper *::before,
.pns-wrapper *::after {
  box-sizing: border-box;
}

.pns-wrapper svg {
  stroke: currentColor;
  stroke-width: 1.9;
  stroke-linecap: round;
  stroke-linejoin: round;
}

/* SEARCH */

.pns-form {
  display: flex;
  align-items: center;
  position: relative;
  width: 100%;
  height: 47px;
  padding: 0;
  border-radius: 9px;
  background: #f5f7f9;
  border: 1px solid #e3e7eb;
  margin: 0;
  transition: border-color .2s, box-shadow .2s;
}

.pns-form:focus-within {
  background: white;
  border-color: #ff7900;
  box-shadow: 0 0 0 3px rgba(255, 121, 0, .09);
}

.pns-input-icon {
  position: absolute;
  left: 15px;
  width: 17px;
  height: 17px;
  color: #939da5;
  pointer-events: none;
}

.pns-form input {
  width: 100%;
  height: 100%;
  border: 0;
  outline: none;
  background: transparent;
  color: #242a30;
  font-family: inherit;
  font-size: 12px;
  padding: 0 75px 0 43px;
  min-width: 0;
}

.pns-form input::placeholder {
  color: #9ba4ad;
}

.pns-form input::-webkit-search-cancel-button {
  display: none;
}

.pns-clear {
  position: absolute;
  right: 57px;
  border: 0;
  background: transparent;
  color: #8c949c;
  font-size: 22px;
  line-height: 1;
  cursor: pointer;
}

.pns-submit {
  position: absolute;
  top: 4px;
  right: 4px;
  bottom: 4px;
  display: grid;
  place-items: center;
  height: auto;
  width: 43px;
  background: #ff7900;
  color: #fff;
  border: 0;
  border-radius: 7px;
  cursor: pointer;
}

.pns-submit:hover {
  background: #e76e00;
}

.pns-submit svg {
  width: 18px;
  height: 18px;
}

/* DROPDOWN */

.pns-dropdown {
  position: absolute;
  top: calc(100% + 9px);
  left: 0;
  z-index: 9999;
  width: 100%;
  min-width: min(430px, 90vw);
  max-height: min(610px, 75vh);
  overflow-y: auto;
  overflow-x: hidden;
  background: #fff;
  border: 1px solid #e8ebef;
  border-radius: 13px;
  box-shadow: 0 22px 60px rgba(20, 26, 32, .17);
  color: #252b31;
}

.pns-dropdown-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 18px;
  background: #f8f9fb;
  border-bottom: 1px solid #ebedf0;
}

.pns-dropdown-header > span {
  display: flex;
  align-items: center;
  gap: 9px;
  font-size: 12px;
  font-weight: 850;
}

.pns-dropdown-header svg {
  width: 17px;
  height: 17px;
  color: #ff7900;
}

.pns-dropdown-header small {
  color: #9199a1;
  font-size: 10px;
}

.pns-section-title {
  display: block;
  color: #929aa2;
  font-size: 9px;
  letter-spacing: 1.2px;
  font-weight: 900;
}

.pns-brand-section {
  padding: 16px 17px 15px;
  border-bottom: 1px solid #edf0f2;
}

.pns-brand-list {
  display: flex;
  flex-wrap: wrap;
  gap: 7px;
  margin-top: 11px;
}

.pns-brand-chip {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 11px;
  border: 1px solid #fbd6b6;
  border-radius: 6px;
  background: #fff7f0;
  color: #d46a09;
  font-family: inherit;
  font-size: 10px;
  font-weight: 800;
  cursor: pointer;
}

.pns-brand-chip:hover {
  background: #ff7900;
  border-color: #ff7900;
  color: white;
}

.pns-brand-chip span {
  font-size: 13px;
}

.pns-results {
  padding: 8px 0;
}

.pns-section-heading {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 9px 18px 12px;
}

.pns-section-heading small {
  color: #a2aab1;
  font-size: 10px;
}

/* RESULT ITEM */

.pns-result-item {
  width: 100%;
  display: flex;
  align-items: center;
  gap: 13px;
  padding: 12px 17px;
  border: 0;
  border-bottom: 1px solid #f0f2f4;
  background: #fff;
  text-align: left;
  font-family: inherit;
  cursor: pointer;
  transition: background .15s;
}

.pns-result-item:last-child {
  border-bottom: 0;
}

.pns-result-item:hover,
.pns-result-item.selected {
  background: #fff7f0;
}

.pns-product-image {
  display: grid;
  place-items: center;
  flex-shrink: 0;
  width: 58px;
  height: 58px;
  border-radius: 9px;
  background: #f5f6f8;
  overflow: hidden;
}

.pns-product-image img {
  max-width: 90%;
  max-height: 90%;
  object-fit: contain;
}

.pns-product-image svg {
  width: 32px;
  height: 32px;
  color: #a2abb3;
}

.pns-product-info {
  display: flex;
  flex-direction: column;
  gap: 5px;
  min-width: 0;
  flex: 1;
}

.pns-product-info > strong {
  color: #242b31;
  font-size: 12px;
  font-weight: 850;
  line-height: 1.5;
}

.pns-product-specs {
  color: #808a92;
  font-size: 10px;
  line-height: 1.6;
}

.pns-dot {
  padding: 0 3px;
  color: #ff7900;
}

.pns-product-tag {
  align-self: flex-start;
  padding: 3px 7px;
  background: #f0f2f4;
  color: #64707a;
  border-radius: 4px;
  font-size: 9px;
  font-weight: 750;
}

.pns-product-right {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  justify-content: center;
  gap: 5px;
  flex-shrink: 0;
}

.pns-product-right strong {
  color: #151b21;
  font-size: 15px;
  font-weight: 900;
  white-space: nowrap;
}

.pns-product-right small {
  color: #ff7900;
  font-size: 9px;
  font-weight: 750;
}

.pns-product-right svg {
  display: none;
  width: 16px;
  height: 16px;
  color: #ff7900;
}

/* EMPTY */

.pns-empty {
  padding: 33px 18px;
  text-align: center;
}

.pns-empty-icon {
  display: grid;
  place-items: center;
  margin: 0 auto 13px;
  width: 48px;
  height: 48px;
  background: #fff1e5;
  border-radius: 50%;
  color: #ff7900;
}

.pns-empty-icon svg {
  width: 24px;
  height: 24px;
}

.pns-empty strong {
  font-size: 12px;
  color: #30373d;
}

.pns-empty p {
  font-size: 11px;
  line-height: 1.7;
  color: #8a949e;
  margin: 8px auto 0;
  max-width: 260px;
}

/* BOTTOM LINK */

.pns-view-all {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  width: 100%;
  padding: 17px 19px;
  border: 0;
  border-top: 1px solid #e9edf0;
  background: #f8f9fb;
  color: #434c55;
  text-align: left;
  font-family: inherit;
  font-size: 11px;
  cursor: pointer;
}

.pns-view-all strong {
  color: #ff7900;
}

.pns-view-all:hover {
  background: #fff2e6;
}

.pns-view-all svg {
  width: 18px;
  height: 18px;
  color: #ff7900;
  flex-shrink: 0;
}

/* ANIMATION */

.pns-dropdown-enter-active,
.pns-dropdown-leave-active {
  transition: opacity .15s, transform .15s;
}

.pns-dropdown-enter,
.pns-dropdown-leave-to {
  opacity: 0;
  transform: translateY(-6px);
}

/* RESPONSIVE */

@media (max-width: 767px) {
  .pns-dropdown {
    min-width: 0;
    width: 100%;
  }

  .pns-product-image {
    width: 46px;
    height: 46px;
  }

  .pns-result-item {
    gap: 9px;
    padding: 11px;
  }

  .pns-product-info > strong {
    font-size: 11px;
  }

  .pns-product-right strong {
    font-size: 12px;
  }

  .pns-product-right small {
    display: none;
  }

  .pns-product-right svg {
    display: block;
  }
}

@media (prefers-reduced-motion: reduce) {
  .pns-dropdown-enter-active,
  .pns-dropdown-leave-active {
    transition: none;
  }
}
</style>
